import { defaultPaginatedResponse, mapPaginatedResponse } from '@/mappers/common.mapper';
import {
  mapCurrentUserResponse,
  mapInviteUserRequest,
  mapUpdateProfileRequest,
  mapUserInvitationResponse,
  mapUserResponse,
} from '@/mappers/user.mapper';

import type { InviteUserFormValues, UpdateUserProfileFormValues } from '@/schemas/user.schema';

import apiClient from '@/lib/api-client';
import { raiseAxiosErrorOrDefault } from '@/lib/error';
import { buildQueryParams } from '@/lib/utils';

import type { GenericResponse, PaginatedResponse, PaginationParams } from '@/types/common.type';
import type { CurrentUser, User, UserInvitation } from '@/types/user.type';

export const fetchAllUsers = async (
  params?: PaginationParams
): Promise<PaginatedResponse<User>> => {
  try {
    return await apiClient.get<PaginatedResponse<User>>(
      '/users',
      mapPaginatedResponse(mapUserResponse),
      {
        params: buildQueryParams(params),
      }
    );
  } catch {
    return defaultPaginatedResponse<User>();
  }
};

export const deleteUserById = async (userId: number): Promise<void> => {
  try {
    await apiClient.delete(`/users/${userId}`);
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to delete user');
  }
};

export const updateUserRole = async (userId: number, role: string): Promise<void> => {
  try {
    await apiClient.patch<void>(`/users/assign-role`, { role_id: Number(role), user_id: userId });
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to update user role');
  }
};

export const getCurrentUser = async (): Promise<CurrentUser> => {
  try {
    const response = await apiClient.get<CurrentUser>('/users/me', mapCurrentUserResponse);
    return response;
  } catch (error) {
    raiseAxiosErrorOrDefault(error, 'Failed to fetch current user');
  }
};

export const updateUserProfile = async (
  data: UpdateUserProfileFormValues
): Promise<CurrentUser> => {
  try {
    const response = await apiClient.patch<CurrentUser>(
      '/users/me',
      mapUpdateProfileRequest(data),
      mapCurrentUserResponse
    );
    return response;
  } catch (error) {
    raiseAxiosErrorOrDefault(error, 'Failed to update user profile');
  }
};

export const fetchUserInvitations = async (
  params?: PaginationParams
): Promise<PaginatedResponse<UserInvitation>> => {
  try {
    return await apiClient.get<PaginatedResponse<UserInvitation>>(
      '/users/invitations',
      mapPaginatedResponse(mapUserInvitationResponse),
      {
        params: buildQueryParams(params),
      }
    );
  } catch {
    return defaultPaginatedResponse<UserInvitation>();
  }
};

export const inviteUser = async (data: InviteUserFormValues): Promise<string> => {
  try {
    const response = await apiClient.post<GenericResponse<string>>(
      '/users/invite',
      mapInviteUserRequest(data)
    );

    return response.data;
  } catch (error) {
    raiseAxiosErrorOrDefault(error, 'Failed to invite user');
  }
};

export const resendInvitation = async (userId: number): Promise<void> => {
  try {
    await apiClient.post(`/users/${userId}/resend-invitation`);
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to resend invitation');
  }
};

export const cancelInvitation = async (invitationId: number): Promise<void> => {
  try {
    await apiClient.delete(`/users/invitations/${invitationId}`);
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to cancel invitation');
  }
};
